import React, { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, Star, Quote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function TestimonialsPage() {
  const navigate = useNavigate();
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [matrixLayers, setMatrixLayers] = useState<JSX.Element[][]>(Array(4).fill([]));
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const createMatrixColumn = useCallback((index: number, layerIndex: number) => {
    const katakana = 'アァカサタナハマヤャラワガザダバパイィキシチニヒミリヰギジヂビピウゥクスツヌフムユュルグズブヅプエェケセテネヘメレヱゲゼデベペオォコソトノホモヨョロヲゴゾドボポヴッン';
    const latin = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const nums = '0123456789';
    const alphabet = katakana + latin + nums;
    
    const columnLength = Math.floor(window.innerHeight / 10) + 25;
    
    const column = Array(columnLength).fill(0).map(() => {
      return Math.random() > 0.5 ? alphabet.charAt(Math.floor(Math.random() * alphabet.length)) : ' ';
    }).join('\n');
    
    const baseDuration = 15.84375;
    const duration = baseDuration + (layerIndex * 2.640625);
    const delay = (-Math.random() * 7.8125) * 1.3;
    const columnCount = isMobile ? 52 : 105;
    const left = `${(index * (100/columnCount))}%`;
    
    return (
      <div
        key={`column-${layerIndex}-${index}-${Date.now()}`}
        className="matrix-column testimonial-matrix"
        style={{
          left,
          animation: `matrixRain ${duration}s ${delay}s linear infinite`
        }}
      >
        {column}
      </div>
    );
  }, [isMobile]);

  useEffect(() => {
    const initializeLayers = () => {
      const columnCount = isMobile ? 52 : 105;
      const newLayers = Array(4).fill(0).map((_, layerIndex) => 
        Array(columnCount).fill(0).map((_, columnIndex) => createMatrixColumn(columnIndex, layerIndex))
      );
      setMatrixLayers(newLayers);
    };

    initializeLayers();

    const intervals = Array(4).fill(0).map((_, layerIndex) => 
      setInterval(() => {
        const columnCount = isMobile ? 52 : 105;
        const randomColumn = Math.floor(Math.random() * columnCount);
        setMatrixLayers(prev => {
          const newLayers = [...prev];
          const newLayer = [...newLayers[layerIndex]];
          newLayer[randomColumn] = createMatrixColumn(randomColumn, layerIndex);
          newLayers[layerIndex] = newLayer;
          return newLayers;
        });
      }, 281.25 + layerIndex * 93.75)
    );

    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768;
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
        initializeLayers();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      intervals.forEach(interval => clearInterval(interval));
      window.removeEventListener('resize', handleResize);
    };
  }, [createMatrixColumn, isMobile]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="relative min-h-screen bg-black text-white overflow-hidden">
      <div className="matrix-rain">
        {matrixLayers.map((layer, i) => (
          <div key={`layer-${i}`} className={`matrix-layer matrix-layer-${i + 1}`}>
            {layer}
          </div>
        ))}
      </div>
      
      <div
        className="cursor-glow"
        style={{
          left: `${cursorPosition.x}px`,
          top: `${cursorPosition.y}px`,
        }}
      />
      
      <div className="relative z-10">
        <nav className="fixed top-0 w-full px-6 py-4 bg-black bg-opacity-70 backdrop-blur-sm">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white hover:text-green-400 transition-colors duration-300"
          >
            <ArrowLeft size={20} />
            Back to Main Page
          </button>
        </nav>

        <div className="container mx-auto px-8 md:px-12 lg:px-16 pt-24 pb-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-2 sm:space-y-3 md:space-y-4 mb-12">
              <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold tracking-wider glitch-text text-green-400 whitespace-nowrap">
                WHAT OUR USERS SAY
              </h1>
              <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold tracking-wider glitch-text text-green-400 whitespace-nowrap">
                
              </h1>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 md:gap-12">
              {[
                {
                  id: 1,
                  name: "Sarah Chen",
                  role: "AI Research Lead",
                  company: "TechVision Labs",
                  rating: 5,
                  avatar: "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&w=150&h=150&fit=crop"
                },
                {
                  id: 2,
                  name: "Marcus Rodriguez",
                  role: "CTO",
                  company: "Future Systems",
                  rating: 5,
                  avatar: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&w=150&h=150&fit=crop"
                },
                {
                  id: 3,
                  name: "Emily Watson",
                  role: "Lead Developer",
                  company: "InnovateAI",
                  rating: 5,
                  avatar: "https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&w=150&h=150&fit=crop"
                },
                {
                  id: 4,
                  name: "David Park",
                  role: "AI Engineer",
                  company: "NextGen AI",
                  rating: 5,
                  avatar: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&w=150&h=150&fit=crop"
                }
              ].map((testimonial) => (
                <div 
                  key={testimonial.id} 
                  className="bg-gray-900/50 rounded-lg p-6 backdrop-blur-sm border border-green-400/20 hover:border-green-400 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="text-base sm:text-lg md:text-xl font-semibold text-white">{testimonial.name}</h3>
                      <p className="text-sm sm:text-base text-white/80">{testimonial.role}</p>
                      <p className="text-xs sm:text-sm text-white/60">{testimonial.company}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-1 mb-4">
                    {Array(testimonial.rating).fill(0).map((_, i) => (
                      <Star key={i} size={14} className="text-white fill-white sm:w-4 sm:h-4" />
                    ))}
                  </div>

                  <div className="relative">
                    <Quote className="absolute -top-2 -left-2 w-4 h-4 sm:w-6 sm:h-6 text-white/20" />
                    <p className="text-white/80 mb-4 pl-6 text-sm sm:text-base md:text-lg font-medium">Coming Soon</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TestimonialsPage;